import * as React from "react"
import { Loader2, Mail, Palmtree } from "lucide-react"
import { toast } from "sonner"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { useBookingT } from "@/hooks/use-translations"
import { formatUserDateTime } from "@/lib/date-time"
import { grantConsent, withdrawConsent } from "@/pages/legal-documents/api"
import {
  cachedRead,
  fetchLeaveOffers,
  updateConnection,
  type HrisConnection,
  type HrisLeaveOffers,
} from "@/features/integrations/hris/hrisApi"

type Props = {
  connection: HrisConnection
  /** Bumped by the parent when the legal state changes, to reload availability. */
  refreshKey: number
}

/**
 * Schedule E §8: an optional email to employees before their approved leave.
 * Switched on by its own permission (never bundled with the HR-data one), and
 * each employee still has to opt in from their notification settings. The
 * company sees the letter, never who receives it.
 */
export function HrisLeaveOffersCard({ connection, refreshKey }: Props) {
  const t = useBookingT()
  const [state, setState] = React.useState<HrisLeaveOffers | null>(() => cachedRead<HrisLeaveOffers>(`leave-offers:${connection.id}`) ?? null)
  const [dialog, setDialog] = React.useState<"enable" | "disable" | "preview" | null>(null)
  const [affirmed, setAffirmed] = React.useState(false)
  const [busy, setBusy] = React.useState(false)

  const load = React.useCallback(() => {
    fetchLeaveOffers(connection.id)
      .then(setState)
      .catch(() => setState(null))
  }, [connection.id])

  React.useEffect(load, [load, refreshKey])

  if (!state) return null

  const run = async (fn: () => Promise<unknown>, done: string) => {
    setBusy(true)
    try {
      await fn()
      toast.success(done)
      setDialog(null)
      setAffirmed(false)
      load()
    } catch (e) {
      toast.error((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  const changeDays = (value: string) =>
    run(
      () => updateConnection(connection.id, { settings: { leaveOfferDaysBefore: Number(value) } }),
      t("hris_offers_saved", "Saved."),
    )

  return (
    <Card>
      <CardContent className="space-y-4 p-6">
        <div className="flex items-start justify-between gap-3 max-md:flex-col">
          <div className="space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <Palmtree className="size-5 text-brand-500" />
              <h2 className="text-lg font-semibold text-neutral-900">{t("hris_offers_title", "Travel ideas before approved leave")}</h2>
              {state.enabled ? (
                <Badge variant="green">{t("hris_offers_on", "On")}</Badge>
              ) : (
                <Badge variant="frozen">{t("hris_offers_off", "Off")}</Badge>
              )}
            </div>
            <p className="max-w-2xl text-sm text-neutral-800">
              {t(
                "hris_offers_intro",
                "An optional email to employees before an approved leave of three days or more, suggesting they book their own trip in Travel Code. Only employees who switch it on in their notification settings receive it. You never see who opted in, received or booked.",
              )}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => setDialog("preview")}>
              <Mail className="size-4" />
              {t("hris_offers_preview", "See the email")}
            </Button>
            {state.enabled ? (
              <Button variant="outline" className="text-error-700" onClick={() => setDialog("disable")}>
                {t("hris_offers_disable", "Switch off")}
              </Button>
            ) : (
              <Button disabled={!state.available} onClick={() => setDialog("enable")}>
                {t("hris_offers_enable", "Switch on")}
              </Button>
            )}
          </div>
        </div>

        {!state.available && (
          <p className="text-sm text-neutral-800">
            {t("hris_offers_needs_legal", "Available once Schedule E and the permission to receive employee data are in force.")}
          </p>
        )}

        <div className="flex flex-wrap items-center gap-x-6 gap-y-3 border-t border-neutral-200 pt-4">
          <div className="flex items-center gap-3">
            <Label className="text-sm text-neutral-800">{t("hris_offers_when", "Send")}</Label>
            <Select value={String(state.daysBefore)} onValueChange={(v) => void changeDays(v)} disabled={busy}>
              <SelectTrigger className="w-[220px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {state.daysOptions.map((d) => (
                  <SelectItem key={d} value={String(d)}>
                    {t("hris_offers_days", "{count} days before the leave").replace("{count}", String(d))}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {state.enabled && state.grantedAt && (
            <p className="text-sm text-neutral-800">
              {t("hris_offers_granted", "Switched on {date} by {who}")
                .replace("{date}", formatUserDateTime(state.grantedAt))
                .replace("{who}", state.grantedBy?.name ?? "—")}
            </p>
          )}
        </div>
      </CardContent>

      <Dialog open={dialog === "preview"} onOpenChange={(open) => !open && setDialog(null)}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t("hris_offers_preview_title", "The email an employee receives")}</DialogTitle>
            <DialogDescription>{t("hris_offers_preview_hint", "Shown with sample leave dates, in your language.")}</DialogDescription>
          </DialogHeader>
          <div className="rounded-md border border-neutral-200">
            <div className="border-b border-neutral-200 px-4 py-3 text-sm">
              <span className="text-neutral-800">{t("hris_offers_subject", "Subject")}: </span>
              <span className="font-medium text-neutral-900">{state.preview.subject}</span>
            </div>
            <iframe
              title={state.preview.subject}
              sandbox=""
              srcDoc={`<body style="font-family:system-ui,sans-serif;font-size:14px;color:#111827;margin:16px">${state.preview.html}</body>`}
              className="h-96 w-full"
            />
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={dialog === "enable"} onOpenChange={(open) => !open && setDialog(null)}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader className="text-left">
            <DialogTitle>{state.consent.name}</DialogTitle>
            <DialogDescription className="text-neutral-800">{state.consent.summary}</DialogDescription>
          </DialogHeader>
          <label className="flex items-start gap-3 text-sm text-neutral-900">
            <Checkbox checked={affirmed} onCheckedChange={(v) => setAffirmed(v === true)} className="mt-0.5" />
            <span>{state.consent.affirmation}</span>
          </label>
          <p className="text-sm text-neutral-800">{state.consent.withdrawal}</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}>
              {t("hris_cancel", "Cancel")}
            </Button>
            <Button
              disabled={!affirmed || busy}
              onClick={() =>
                void run(
                  () => grantConsent(state.consent.code, state.consent.screenVersion),
                  t("hris_offers_enabled", "Switched on. Employees can now opt in from their notification settings."),
                )
              }
            >
              {busy && <Loader2 className="size-4 animate-spin" />}
              {t("hris_offers_enable", "Switch on")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dialog === "disable"} onOpenChange={(open) => !open && setDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("hris_offers_disable_title", "Switch off travel ideas?")}</DialogTitle>
            <DialogDescription>{state.consent.withdrawal}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}>
              {t("hris_cancel", "Cancel")}
            </Button>
            <Button
              variant="destructive"
              disabled={busy}
              onClick={() => void run(() => withdrawConsent(state.consent.code), t("hris_offers_disabled", "Switched off. No more emails are sent."))}
            >
              {busy && <Loader2 className="size-4 animate-spin" />}
              {t("hris_offers_disable", "Switch off")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  )
}
